import React, { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  FaPlus, FaEdit, FaTrash, FaSearch, FaStar, FaBuilding
} from 'react-icons/fa';
import { adminApi, formatPrice, getPropertyTypeLabel, getPropertyStatusLabel } from '../../services/api';
import AdminLayout from './AdminLayout';
import './Admin.css';

const AdminProperties = () => {
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminApi.getAllProperties();
      setProperties(res.data?.data || []);
    } catch (_) {
      toast.error('Error al cargar propiedades');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = properties.filter(p =>
    !search ||
    p.title?.toLowerCase().includes(search.toLowerCase()) ||
    p.city?.toLowerCase().includes(search.toLowerCase())
  );

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await adminApi.deleteProperty(deleteId);
      toast.success('Propiedad eliminada');
      setDeleteId(null);
      load();
    } catch (_) {
      toast.error('Error al eliminar');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <AdminLayout title="Propiedades">
      <div className="admin-page-header">
        <div className="admin-search">
          <FaSearch />
          <input
            placeholder="Buscar por título o ciudad..."
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>
        <Link to="/admin12/propiedades/nueva" className="btn btn--gold">
          <FaPlus /> Nueva Propiedad
        </Link>
      </div>

      {loading ? (
        <div className="admin-loading"><div className="spinner" /></div>
      ) : filtered.length === 0 ? (
        <div className="admin-empty-state">
          <FaBuilding />
          <p>{search ? 'Sin resultados para la búsqueda' : 'No hay propiedades aún'}</p>
          {!search && (
            <Link to="/admin12/propiedades/nueva" className="btn btn--navy">
              Agregar primera propiedad
            </Link>
          )}
        </div>
      ) : (
        <div className="admin-table-wrapper">
          <table className="admin-table admin-table--full">
            <thead>
              <tr>
                <th>Propiedad</th>
                <th>Tipo</th>
                <th>Precio</th>
                <th>Ciudad</th>
                <th>Estado</th>
                <th>Destacada</th>
                <th>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(p => (
                <tr key={p.id}>
                  <td>
                    <div className="prop-row">
                      {p.imageUrls?.[0] && (
                        <img src={p.imageUrls[0]} alt={p.title} className="prop-row__thumb" />
                      )}
                      <div className="prop-row__info">
                        <Link to={`/admin12/propiedades/editar/${p.id}`}>
                          {p.title}
                        </Link>
                        <small>{p.address}</small>
                      </div>
                    </div>
                  </td>
                  <td>{getPropertyTypeLabel(p.type)}</td>
                  <td>{formatPrice(p.price, p.currency)}</td>
                  <td>{p.city}</td>
                  <td>
                    <span className={`status-badge status-badge--${p.status?.toLowerCase()}`}>
                      {getPropertyStatusLabel(p.status)}
                    </span>
                  </td>
                  <td>
                    {p.featured && (
                      <FaStar style={{ color: '#c9a84c' }} title="Destacada" />
                    )}
                  </td>
                  <td>
                    <div className="action-btns">
                      <Link
                        to={`/admin12/propiedades/editar/${p.id}`}
                        className="action-btn action-btn--edit"
                        title="Editar"
                      >
                        <FaEdit />
                      </Link>
                      <button
                        className="action-btn action-btn--delete"
                        title="Eliminar"
                        onClick={() => setDeleteId(p.id)}
                      >
                        <FaTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="admin-count">{filtered.length} propiedad(es)</div>

      {/* Modal confirm delete */}
      {deleteId && (
        <div className="modal-overlay">
          <div className="modal">
            <h3>¿Eliminar propiedad?</h3>
            <p>Esta acción no se puede deshacer.</p>
            <div className="modal-actions">
              <button className="btn btn--outline-navy" onClick={() => setDeleteId(null)}>
                Cancelar
              </button>
              <button
                className="btn btn--danger"
                onClick={handleDelete}
                disabled={deleting}
              >
                {deleting ? 'Eliminando...' : 'Eliminar'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
};

export default AdminProperties;
